import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { filter, map, take } from 'rxjs/operators';
import { AccessService } from '../services/access.service';

/**
 * Requires the current user to be a SuperAdmin.
 * Must be used after authGuard.
 */
export const superAdminGuard = () => {
  const accessService = inject(AccessService);
  const router = inject(Router);

  const loaded$ = toObservable(accessService.loaded);

  const allowOrRedirect = () =>
    accessService.isSuperAdmin() || router.createUrlTree(['/']);

  if (accessService.loaded()) {
    return allowOrRedirect();
  }

  return loaded$.pipe(
    filter((loaded) => loaded === true),
    take(1),
    map(allowOrRedirect),
  );
};

/**
 * Waits for access data to load, then allows or redirects to '/'.
 *
 * Every guard here shares this shape: a synchronous fast path when the mirrors
 * have already loaded, and otherwise one emission of `loaded$`. Without the wait
 * a guard would decide against empty membership signals and bounce a legitimate
 * user on a cold page load.
 */
const guardOn = (allow: (access: AccessService) => boolean) => {
  // Both injections happen here, synchronously, while the injection context is
  // still active. `allow` receives the service rather than calling inject()
  // itself, because it may run later — after the gate emits — by which point
  // inject() would throw.
  const accessService = inject(AccessService);
  const router = inject(Router);

  // accessReady, not loaded: `loaded` only tracks the user document, while every
  // predicate here decides from membership. An empty mirror is indistinguishable
  // from one that has not arrived, so gating on `loaded` alone would evaluate
  // against empty signals and deny a legitimate user on the first navigation
  // after signing in.
  const ready$ = toObservable(accessService.accessReady);
  const allowOrRedirect = () =>
    allow(accessService) || router.createUrlTree(['/']);

  if (accessService.accessReady()) {
    return allowOrRedirect();
  }

  return ready$.pipe(
    filter((ready) => ready === true),
    take(1),
    map(allowOrRedirect),
  );
};

/**
 * Requires the user to have a selected business context.
 * Must be used after authGuard.
 *
 * Load-bearing for the business-scoped screens: FormService takes a businessId
 * as an explicit argument, and this guard is what guarantees one exists so the
 * service never has to handle null.
 */
export const businessContextGuard = () =>
  guardOn((access) => access.ensureBusinessContext());

/**
 * SuperAdmin, or a BUSINESS admin of the currently selected business.
 * Use for screens that author business-wide configuration — form templates,
 * branches, business members.
 *
 * Must run after businessContextGuard, which is what guarantees a selection.
 */
export const businessAdminGuard = () =>
  guardOn((access) => access.canManageForms(access.selectedBusinessId()));

/**
 * SuperAdmin, or anyone with standing in any business at any role.
 *
 * Replaces "authGuard only" on business/branch-scoped screens. Note this admits
 * MANAGER — it gates reaching the screen, not what the screen offers; the
 * capability predicates on AccessService do that.
 */
export const businessScopeGuard = () =>
  guardOn((access) => access.hasAnyBusinessScope());

/**
 * SuperAdmin, a BUSINESS admin, or an OWNER of at least one branch in the
 * selected business.
 *
 * Sits between businessScopeGuard (any standing, MANAGER included) and
 * businessAdminGuard (BUSINESS only). Use for screens that decide what work
 * happens or grade whether it happened — Assignments and Reports — as opposed to
 * screens that carry it out.
 *
 * Must run after businessContextGuard, which is what guarantees a selection.
 */
export const ownerScopeGuard = () =>
  guardOn((access) => access.hasOwnerScope(access.selectedBusinessId()));

/**
 * The Compliance screen: any branch role in the SELECTED business.
 *
 * Not businessScopeGuard, which accepts standing in any business at all — this
 * asks about the one in context, so it says no to a business you merely belong
 * to elsewhere. Must run after businessContextGuard.
 */
export const reportsGuard = () =>
  guardOn((access) => access.canViewReports(access.selectedBusinessId()));

/** Task History: the same line as reportsGuard, named apart so they can diverge. */
export const taskHistoryGuard = () =>
  guardOn((access) => access.canViewTaskHistory(access.selectedBusinessId()));
